import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as adminsApi from "../api/admins";
import * as rolesApi from "../api/roles";
import * as teachersApi from "../api/teachers";

const STANDARD_TEACHER_ROLE = "standard_teacher";

function isStandardTeacherRole(roleId, rolesList) {
  if (roleId === "" || roleId == null) return false;
  const role = rolesList.find((r) => String(r.id) === String(roleId));
  return String(role?.name || "").toLowerCase() === STANDARD_TEACHER_ROLE;
}

function isAdminStandardTeacher(admin) {
  return (
    String(admin?.Role?.name || "").toLowerCase() === STANDARD_TEACHER_ROLE
  );
}

export default function AdminAccessControl() {
  const [admins, setAdmins] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [roles, setRoles] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_admins");
  const { hasPermission: canEdit } = useViewPermission("edit_admins");
  const { busy, run } = useMutationLock();
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [roleDraft, setRoleDraft] = useState("");
  const [teacherDraft, setTeacherDraft] = useState("");

  // permission gating handled by PermissionContext hook

  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    title: "",
    roleId: "",
    teacherId: "",
  });
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);

  const fetchAdmins = async (pageArg = page) => {
    try {
      const response = await adminsApi.list({ page: pageArg, limit });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      const withRole = normalized.data.map((a) => ({
        ...a,
        Role: a.Role || a.role || null,
      }));

      setAdmins(withRole);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching admins:", err);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await rolesApi.list({ exportMode: true });
      const normalized = normalizeListResponse(response.data);
      setRoles(normalized.data);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await teachersApi.list({ exportMode: true });
      const normalized = normalizeListResponse(response.data);
      setTeachers(normalized.data);
    } catch (err) {
      console.error("Error fetching teachers:", err);
    }
  };

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    const loadAdmins = async () => {
      try {
        await fetchAdmins(1);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
    fetchTeachers();
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchAdmins(newPage);
  };

  // ----- Add New Admin -----
  const handleNewAdminChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
        if (!newAdmin.name || !newAdmin.email || !newAdmin.title) {
          notificationCtx.show({ message: "Please fill all fields." });
          return;
        }

        const roleId =
          newAdmin.roleId === "" ? null : Number(newAdmin.roleId);
        const teacherId = isStandardTeacherRole(newAdmin.roleId, roles)
          ? newAdmin.teacherId === ""
            ? null
            : Number(newAdmin.teacherId)
          : null;

        await adminsApi.create({
          name: newAdmin.name,
          email: newAdmin.email,
          title: newAdmin.title,
          roleId,
          teacherId,
        });

        setPage(1);
        await fetchAdmins(1);
        setNewAdmin({
          name: "",
          email: "",
          title: "",
          roleId: "",
          teacherId: "",
        });
        setShowAddAdminModal(false);
        notificationCtx.show({
          message: "Admin added successfully!",
          stat: "success",
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to add admin.";

        notificationCtx.show({
          message: errorMessage,
          stat: "error",
        });
      }
    });
  };

  const handleUpdateAdminRole = async (adminId, selectedRoleId, selectedTeacherId) => {
    if (!canEdit) return;

    await run(async () => {
      try {
        const roleId =
          selectedRoleId === "" || selectedRoleId == null
            ? null
            : Number(selectedRoleId);
        const teacherId = isStandardTeacherRole(selectedRoleId, roles)
          ? selectedTeacherId === "" || selectedTeacherId == null
            ? null
            : Number(selectedTeacherId)
          : null;

        await adminsApi.update(adminId, {
          roleId,
          teacherId,
        });

        const nextRole =
          roles.find((role) => role.id === Number(roleId)) || null;

        setAdmins((prev) =>
          prev.map((admin) =>
            admin.id === adminId
              ? {
                  ...admin,
                  roleId,
                  teacherId,
                  Role: nextRole,
                  Teacher: teacherId
                    ? teachers.find((t) => t.id === Number(teacherId)) || null
                    : null,
                }
              : admin,
          ),
        );
        setEditingAdminId(null);
        setRoleDraft("");
        setTeacherDraft("");
        notificationCtx.show({
          message: "Admin access updated successfully.",
          stat: "success",
        });
      } catch (error) {
        notificationCtx.show({
          message:
            error?.response?.data?.message || "Failed to update admin access.",
          stat: "error",
        });
      }
    });
  };

  const handleDeleteAdmin = (adminId) => {
    if (!canEdit) return;
    const confirmDeleteAdmin = async () => {
      try {
        await adminsApi.remove(adminId);

        const nextPage = admins.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchAdmins(nextPage);
        notificationCtx.show({
          message: "Admin deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete admin",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this admin?",
      stat: "warning",
      onConfirm: confirmDeleteAdmin,
    });
  };

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            minHeight: "0",
            height: "109vh",
            overflow: "hidden",
            padding: "10px 50px 0",
          }}
        >
          {/* ==================== PAGE HEADER ==================== */}
          <div
            className="d-flex justify-content-between align-items-center mb-4"
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "18px",
              padding: "22px 28px",
            }}
          >
            <div>
              <h2
                className="mb-1"
                style={{
                    // fontSize: "28px",
                    fontWeight: "700",
                    color: "#302d45",
                  }}
              >
                ALL ADMINS
              </h2>

              <p
                className="mb-0"
                style={{
                  color: "#625d75",
                  fontSize: "14px",
                }}
              >
                Manage administrator accounts and their assigned roles.
              </p>
            </div>

            {canEdit && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setNewAdmin({
                    name: "",
                    email: "",
                    title: "",
                    roleId: "",
                    teacherId: "",
                  });
                  setShowAddAdminModal(true);
                }}
                style={{
                  height: "38px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  color: "#403b55",
                  border: "none",
                  padding: "0 15px",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "13px",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                + ADD ADMIN
              </button>
            )}
          </div>

          {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

          {/* ==================== ADMINS TABLE ==================== */}
          {admins.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                minHeight: "300px",
                backgroundColor: "#f9e1be",
                borderRadius: "18px",
                padding: "40px",
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#d5d1fd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                  color: "#625d75",
                  fontSize: "22px",
                }}
              >
                <i className="fa-solid fa-users" />
              </div>

              <h5
                className="mb-2"
                style={{
                  fontWeight: "600",
                  color: "#3f3c45",
                }}
              >
                No admins available
              </h5>

              <p
                className="mb-0"
                style={{
                  color: "#77727d",
                  fontSize: "14px",
                }}
              >
                Add an administrator to get started.
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    boxShadow: "0 3px 12px rgba(48, 45, 69, 0.08)",
                    border: "1px solid #eeeaf8",
                  }}
              >
                <div
                    style={{
                      width: "100%",
                      height: "100%",
                      overflowX: "auto",
                      overflowY: "auto",
                    }}
                  >
                    <table
                      border="0"
                      cellPadding="10"
                      cellSpacing="0"
                      style={{
                        minWidth: "1100px",
                        width: "100%",
                        margin: "0",
                        tableLayout: "auto",
                        background: "#ffffff",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                    <thead>
                      <tr>
                        <th
                          style={{
                            backgroundColor: "#d5d1fd",
                            fontWeight: "600",
                            padding: "15px 16px",
                            fontSize: "13px",
                            letterSpacing: "0.4px",
                            textAlign: "left",
                            width: "20%",
                          }}
                        >
                          NAME
                        </th>

                        <th
                          style={{
                            backgroundColor: "#d5d1fd",
                            fontWeight: "600",
                            padding: "15px 16px",
                            fontSize: "13px",
                            letterSpacing: "0.4px",
                            textAlign: "left",
                            width: "25%",
                          }}
                        >
                          EMAIL
                        </th>

                        <th
                          style={{
                            backgroundColor: "#d5d1fd",
                            fontWeight: "600",
                            padding: "15px 16px",
                            fontSize: "13px",
                            letterSpacing: "0.4px",
                            textAlign: "left",
                            width: "18%",
                          }}
                        >
                          TITLE
                        </th>

                        <th
                          style={{
                            backgroundColor: "#d5d1fd",
                            fontWeight: "600",
                            padding: "15px 16px",
                            fontSize: "13px",
                            letterSpacing: "0.4px",
                            textAlign: "center",
                            width: "20%",
                          }}
                        >
                          ROLE
                        </th>

                        <th
                          style={{
                            backgroundColor: "#d5d1fd",
                            fontWeight: "600",
                            padding: "15px 16px",
                            fontSize: "13px",
                            letterSpacing: "0.4px",
                            textAlign: "center",
                            width: "17%",
                          }}
                        >
                          ACTIONS
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {admins.map((admin, index) => (
                        <tr
                          key={admin.id}
                          style={{
                            borderBottom:
                              index === admins.length - 1
                                ? "none"
                                : "1px solid #eeeae5",
                          }}
                        >
                          {/* NAME */}
                          <td
                            style={{
                              padding: "16px",
                              fontWeight: "600",
                            }}
                          >
                            {admin.name}
                          </td>

                          {/* EMAIL */}
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                            }}
                          >
                            {admin.email}
                          </td>

                          {/* TITLE */}
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                            }}
                          >
                            {admin.title}
                          </td>

                          {/* ROLE */}
                          <td
                            style={{
                              padding: "16px",
                              textAlign: "center",
                            }}
                          >
                            {editingAdminId === admin.id && canEdit ? (
                              <div
                                className="d-flex flex-column align-items-center justify-content-center"
                                style={{ gap: "8px" }}
                              >
                                <select
                                  className="form-select"
                                  value={roleDraft}
                                  onChange={(e) => {
                                    const nextRoleId = e.target.value;
                                    setRoleDraft(nextRoleId);
                                    if (
                                      !isStandardTeacherRole(nextRoleId, roles)
                                    ) {
                                      setTeacherDraft("");
                                    }
                                  }}
                                  style={{
                                    maxWidth: "200px",
                                    fontSize: "13px",
                                    borderRadius: "9px",
                                    border: "1px solid #ddd8d2",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <option value="">No role selected</option>

                                  {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                      {role.label}
                                    </option>
                                  ))}
                                </select>

                                {isStandardTeacherRole(roleDraft, roles) ? (
                                  <select
                                    className="form-select"
                                    value={teacherDraft}
                                    onChange={(e) =>
                                      setTeacherDraft(e.target.value)
                                    }
                                    style={{
                                      maxWidth: "200px",
                                      fontSize: "13px",
                                      borderRadius: "9px",
                                      border: "1px solid #ddd8d2",
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    <option value="">No teacher link</option>
                                    {teachers.map((teacher) => (
                                      <option
                                        key={teacher.id}
                                        value={teacher.id}
                                      >
                                        {teacher.name}
                                      </option>
                                    ))}
                                  </select>
                                ) : null}

                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: "8px" }}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    disabled={busy}
                                    onClick={() =>
                                      handleUpdateAdminRole(
                                        admin.id,
                                        roleDraft,
                                        teacherDraft,
                                      )
                                    }
                                    style={{
                                      backgroundColor: "#bff1db",
                                      borderColor: "#bff1db",
                                      borderRadius: "8px",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 11px",
                                    }}
                                  >
                                    {busy ? "Saving..." : "Save"}
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => {
                                      setEditingAdminId(null);
                                      setRoleDraft("");
                                      setTeacherDraft("");
                                    }}
                                    style={{
                                      backgroundColor: "#f9e1be",
                                      borderColor: "#f9e1be",
                                      color: "#625d75",
                                      borderRadius: "8px",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 11px",
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                className="d-flex flex-wrap align-items-center justify-content-center"
                                style={{ gap: "8px" }}
                              >
                                <span
                                  style={{
                                    backgroundColor: "#f9e1be",
                                    color: "#625d75",
                                    borderRadius: "8px",
                                    padding: "6px 11px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {admin.Role?.label || "No role assigned"}
                                </span>
                                {isAdminStandardTeacher(admin) ? (
                                  <span
                                    style={{
                                      backgroundColor: "#eeeaf5",
                                      color: "#514b65",
                                      borderRadius: "8px",
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {admin.Teacher?.name ||
                                      (admin.teacherId
                                        ? `Teacher #${admin.teacherId}`
                                        : "No teacher link")}
                                  </span>
                                ) : null}

                                {canEdit && (
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => {
                                      setEditingAdminId(admin.id);
                                      setRoleDraft(
                                        admin.roleId
                                          ? String(admin.roleId)
                                          : "",
                                      );
                                      setTeacherDraft(
                                        admin.teacherId
                                          ? String(admin.teacherId)
                                          : "",
                                      );
                                    }}
                                    style={{
                                      backgroundColor: "#f9e1be",
                                      borderColor: "#f9e1be",
                                      borderRadius: "8px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      padding: "6px 10px",
                                    }}
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            )}
                          </td>

                          {/* ACTIONS */}
                          <td
                            style={{
                              padding: "16px",
                              textAlign: "center",
                            }}
                          >
                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => handleDeleteAdmin(admin.id)}
                                style={{
                                  backgroundColor: "#f2c4c4",
                                  borderColor: "#f2c4c4",
                                  borderRadius: "9px",
                                  fontWeight: "600",
                                  fontSize: "12px",
                                  padding: "7px 13px",
                                }}
                              >
                                REMOVE
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ==================== PAGINATION ==================== */}
              <PaginationFooter
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="admins"
              />
            </>
          )}

          {/* ========================================================= */}
          {/* ==================== ADD ADMIN MODAL ==================== */}
          {/* ========================================================= */}

          {canEdit && showAddAdminModal && (
            <div
              className="modal fade show"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                zIndex: 10500,
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  margin: "20px",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    border: "none",
                    borderRadius: "20px",
                    overflow: "hidden",
                    boxShadow: "0 10px 35px rgba(0,0,0,0.15)",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {/* MODAL HEADER */}
                  <div
                    className="modal-header"
                    style={{
                      backgroundColor: "#d5d1fd",
                      border: "none",
                      padding: "20px 24px",
                    }}
                  >
                    <div>
                      <h5
                        className="modal-title mb-1"
                        style={{
                          fontWeight: "700",
                          color: "#2f2d35",
                        }}
                      >
                        ADD ADMIN
                      </h5>

                      <p
                        className="mb-0"
                        style={{
                          fontSize: "13px",
                          color: "#625d75",
                        }}
                      >
                        Create a new administrator account.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowAddAdminModal(false)}
                      aria-label="Close"
                    />
                  </div>

                  {/* MODAL BODY */}
                  <div
                    className="modal-body"
                    style={{
                      padding: "26px",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      {/* NAME */}
                      <div className="mb-3">
                        <label
                          htmlFor="adminName"
                          className="form-label"
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#4d4954",
                          }}
                        >
                          Admin Name
                        </label>

                        <input
                          id="adminName"
                          type="text"
                          name="name"
                          value={newAdmin.name}
                          onChange={handleNewAdminChange}
                          required
                          className="form-control"
                          placeholder="Enter admin name"
                          style={{
                            borderRadius: "10px",
                            border: "1px solid #ddd8d2",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        />
                      </div>

                      {/* EMAIL */}
                      <div className="mb-3">
                        <label
                          htmlFor="adminEmail"
                          className="form-label"
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#4d4954",
                          }}
                        >
                          Email
                        </label>

                        <input
                          id="adminEmail"
                          type="email"
                          name="email"
                          value={newAdmin.email}
                          onChange={handleNewAdminChange}
                          required
                          className="form-control"
                          placeholder="Enter admin email"
                          style={{
                            borderRadius: "10px",
                            border: "1px solid #ddd8d2",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        />
                      </div>

                      {/* TITLE */}
                      <div className="mb-3">
                        <label
                          htmlFor="adminTitle"
                          className="form-label"
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#4d4954",
                          }}
                        >
                          Admin Title
                        </label>

                        <input
                          id="adminTitle"
                          type="text"
                          name="title"
                          value={newAdmin.title}
                          onChange={handleNewAdminChange}
                          required
                          className="form-control"
                          placeholder="e.g. Operations Manager"
                          style={{
                            borderRadius: "10px",
                            border: "1px solid #ddd8d2",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        />
                      </div>

                      {/* ROLE */}
                      <div className="mb-3">
                        <label
                          htmlFor="adminRole"
                          className="form-label"
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#4d4954",
                          }}
                        >
                          Role
                        </label>

                        <select
                          id="adminRole"
                          className="form-select"
                          value={newAdmin.roleId}
                          onChange={(e) => {
                            const nextRoleId = e.target.value;
                            setNewAdmin((prev) => ({
                              ...prev,
                              roleId: nextRoleId,
                              teacherId: isStandardTeacherRole(
                                nextRoleId,
                                roles,
                              )
                                ? prev.teacherId
                                : "",
                            }));
                          }}
                          style={{
                            borderRadius: "10px",
                            border: "1px solid #ddd8d2",
                            padding: "10px 12px",
                            fontSize: "14px",
                          }}
                        >
                          <option value="">No role selected</option>

                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* TEACHER LINK — only for standard_teacher */}
                      {isStandardTeacherRole(newAdmin.roleId, roles) ? (
                        <div className="mb-4">
                          <label
                            htmlFor="adminTeacher"
                            className="form-label"
                            style={{
                              fontWeight: "600",
                              fontSize: "13px",
                              color: "#4d4954",
                            }}
                          >
                            Linked teacher (for attendance)
                          </label>

                          <select
                            id="adminTeacher"
                            className="form-select"
                            value={newAdmin.teacherId}
                            onChange={(e) =>
                              setNewAdmin((prev) => ({
                                ...prev,
                                teacherId: e.target.value,
                              }))
                            }
                            style={{
                              borderRadius: "10px",
                              border: "1px solid #ddd8d2",
                              padding: "10px 12px",
                              fontSize: "14px",
                            }}
                          >
                            <option value="">No teacher link</option>
                            {teachers.map((teacher) => (
                              <option key={teacher.id} value={teacher.id}>
                                {teacher.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}

                      {/* ACTIONS */}
                      <div
                        className="d-flex justify-content-end"
                        style={{ gap: "10px" }}
                      >
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setShowAddAdminModal(false)}
                          style={{
                            backgroundColor: "#f9e1be",
                            borderColor: "#f9e1be",
                            borderRadius: "10px",
                            padding: "9px 18px",
                            fontWeight: "600",
                            color: "#625d75",
                          }}
                        >
                          CANCEL
                        </button>

                        <button
                          type="submit"
                          className="btn"
                          disabled={
                            busy ||
                            !newAdmin.name ||
                            !newAdmin.email ||
                            !newAdmin.title
                          }
                          style={{
                            backgroundColor: "#f2c4c4",
                            borderColor: "#f2c4c4",
                            borderRadius: "10px",
                            padding: "9px 20px",
                            fontWeight: "600",
                            color: "#2f2d35",
                          }}
                        >
                          {busy ? "Working..." : "ADD ADMIN"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
